/**
 * ExpandableTable — built on Fluent UI DataGrid.
 *
 * DataGrid renders as flex divs (not <table>), avoiding the
 * tableLayout:fixed issue that caused column squeeze with colSpan
 * expanded rows. The expanded content is rendered as a plain div
 * between DataGridRows, outside the grid column layout entirely.
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  Input,
  Spinner,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { TableColumnDefinition } from '@fluentui/react-components';
import {
  FilterRegular,
  ArrowSortUpRegular,
  ArrowSortDownRegular,
  ChevronRightRegular,
  ChevronDownRegular,
  SearchRegular,
} from '@fluentui/react-icons';

export interface Column<T> {
  key: string;
  label: string;
  render: (item: T) => React.ReactNode;
  filterable?: boolean;
  sortable?: boolean;
  width?: string;
  getValue?: (item: T) => string;
  /** Hide this column on screens ≤ 768px */
  hideOnMobile?: boolean;
}

export interface ExpandableTableProps<T> {
  columns: Column<T>[];
  items: T[];
  /**
   * Renderer for the expanded panel under a row. Receives the row's item
   * AND the current debounced search text so the renderer can filter
   * sub-items (e.g. nested deployments) to match the top-level search.
   * Pass an empty string is a no-op for renderers that don't filter.
   */
  renderExpanded: (item: T, searchText: string) => React.ReactNode;
  onRowClick?: (item: T) => void;
  getRowKey: (item: T) => string;
  loading?: boolean;
  emptyMessage?: string;
  searchPlaceholder?: string;
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  /**
   * Optional supplemental text to include in search matching, on top of the
   * per-column `getValue` lookups. Use this to expose data that isn't shown
   * in any column but should still be searchable - e.g. model names,
   * capability tokens, friendly aliases. The returned string is matched
   * case-insensitively against the search input via `includes()`.
   */
  getSearchableText?: (item: T) => string;
}

type SortDir = 'asc' | 'desc';

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  searchRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    userSelect: 'none',
    width: '100%',
  },
  clickableRow: { cursor: 'pointer', '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  expandedContent: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalM,
    overflowX: 'auto',
  },
  filterList: {
    maxHeight: '240px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '180px',
  },
  chevron: { flexShrink: 0, width: '20px', display: 'inline-flex', alignItems: 'center' },
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: tokens.spacingVerticalXXL },
  searchInput: { minWidth: '300px', '@media (max-width: 480px)': { minWidth: '100%' } },
  filterPopoverContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  mobileHidden: { '@media (max-width: 768px)': { display: 'none' } },
});

export function ExpandableTable<T>(props: ExpandableTableProps<T>) {
  const {
    columns,
    items,
    renderExpanded,
    onRowClick,
    getRowKey,
    loading,
    emptyMessage = 'No items to display.',
    searchPlaceholder = 'Search…',
    defaultSortKey,
    defaultSortDir = 'asc',
    getSearchableText,
  } = props;

  const styles = useStyles();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir);
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});

  const getColumnValue = useCallback(
    (col: Column<T>, item: T): string => {
      if (col.getValue) return col.getValue(item);
      const node = col.render(item);
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      return '';
    },
    [],
  );

  // Unique values per filterable column
  const uniqueValues = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const col of columns) {
      if (!col.filterable) continue;
      const vals = new Set<string>();
      for (const item of items) {
        const v = getColumnValue(col, item);
        if (v) vals.add(v);
      }
      map[col.key] = Array.from(vals).sort();
    }
    return map;
  }, [columns, items, getColumnValue]);

  // Filtering + searching + sorting
  const processed = useMemo(() => {
    let result = [...items];

    for (const [colKey, selected] of Object.entries(filters)) {
      if (selected.size === 0) continue;
      const col = columns.find((c) => c.key === colKey);
      if (!col) continue;
      result = result.filter((item) => selected.has(getColumnValue(col, item)));
    }

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      result = result.filter((item) => {
        // Match against any column's getValue first (fast path - most matches
        // come from visible content).
        if (columns.some((col) => getColumnValue(col, item).toLowerCase().includes(q))) {
          return true;
        }
        // Fall back to supplemental searchable text (e.g. model names,
        // capability tokens) that isn't displayed in any column.
        if (getSearchableText) {
          return getSearchableText(item).toLowerCase().includes(q);
        }
        return false;
      });
    }

    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col) {
        result.sort((a, b) => {
          const va = getColumnValue(col, a).toLowerCase();
          const vb = getColumnValue(col, b).toLowerCase();
          const cmp = va < vb ? -1 : va > vb ? 1 : 0;
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [items, columns, filters, debouncedSearch, sortKey, sortDir, getColumnValue, getSearchableText]);

  const handleSort = useCallback((colKey: string) => {
    setSortKey((prev) => {
      if (prev === colKey) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return colKey;
    });
  }, []);

  const toggleFilter = useCallback((colKey: string, value: string) => {
    setFilters((prev) => {
      const current = new Set(prev[colKey] ?? []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [colKey]: current };
    });
  }, []);

  const clearFilter = useCallback((colKey: string) => {
    setFilters((prev) => {
      const copy = { ...prev };
      delete copy[colKey];
      return copy;
    });
  }, []);

  const toggleRow = useCallback((key: string, item: T) => {
    setExpandedKey((prev) => (prev === key ? null : key));
    onRowClick?.(item);
  }, [onRowClick]);

  // Build DataGrid column definitions
  const dgColumns: TableColumnDefinition<T>[] = useMemo(() => {
    const cols: TableColumnDefinition<T>[] = [];

    // Chevron column
    cols.push(
      createTableColumn<T>({
        columnId: '__expand',
        renderHeaderCell: () => null,
        renderCell: () => null, // Rendered in the row directly
      }),
    );

    for (const col of columns) {
      cols.push(
        createTableColumn<T>({
          columnId: col.key,
          renderHeaderCell: () => null, // Custom header rendering
          renderCell: (item) => col.render(item),
        }),
      );
    }

    return cols;
  }, [columns]);

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="medium" label="Loading…" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchRow}>
        <Input
          contentBefore={<SearchRegular />}
          value={search}
          onChange={(_, d) => {
            setSearch(d.value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => setDebouncedSearch(d.value), 250);
          }}
          placeholder={searchPlaceholder}
          className={styles.searchInput}
        />
        <Text size={200}>
          {processed.length} of {items.length} item{items.length === 1 ? '' : 's'}
        </Text>
      </div>

      {items.length === 0 ? (
        <div className={styles.centered}>
          <Text>{emptyMessage}</Text>
        </div>
      ) : (
        <DataGrid
          items={processed}
          columns={dgColumns}
          getRowId={(item) => getRowKey(item)}
          aria-label="expandable table"
        >
          <DataGridHeader>
            <DataGridRow>
              {({ columnId }) => (
                <DataGridHeaderCell
                  style={columnId === '__expand' ? { maxWidth: 28, minWidth: 28, padding: '0 2px' } : undefined}
                  className={columns.find(c => c.key === columnId)?.hideOnMobile ? styles.mobileHidden : undefined}
                >
                  {columnId === '__expand' ? null : (() => {
                    const col = columns.find(c => c.key === columnId);
                    if (!col) return null;
                    return (
                      <div className={styles.headerCell}>
                        <span
                          onClick={() => col.sortable && handleSort(col.key)}
                          style={col.sortable ? { cursor: 'pointer' } : undefined}
                        >
                          {col.label}
                        </span>
                        {col.sortable && sortKey === col.key && (
                          sortDir === 'asc' ? <ArrowSortUpRegular fontSize={14} /> : <ArrowSortDownRegular fontSize={14} />
                        )}
                        {col.filterable && uniqueValues[col.key]?.length > 0 && (
                          <FilterDropdown
                            values={uniqueValues[col.key]}
                            selected={filters[col.key] ?? new Set()}
                            onToggle={(v) => toggleFilter(col.key, v)}
                            onClear={() => clearFilter(col.key)}
                          />
                        )}
                      </div>
                    );
                  })()}
                </DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<T>>
            {({ item, rowId }) => {
              const isExpanded = expandedKey === String(rowId);
              return (
                <React.Fragment key={String(rowId)}>
                  <DataGridRow<T>
                    className={styles.clickableRow}
                    onClick={() => toggleRow(String(rowId), item)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRow(String(rowId), item);
                      }
                    }}
                    tabIndex={0}
                    aria-expanded={isExpanded}
                  >
                    {({ columnId }) => (
                      <DataGridCell
                        style={columnId === '__expand' ? { maxWidth: 28, minWidth: 28, padding: '0 2px' } : undefined}
                        className={columns.find(c => c.key === columnId)?.hideOnMobile ? styles.mobileHidden : undefined}
                      >
                        {columnId === '__expand' ? (
                          <span className={styles.chevron}>
                            {isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                          </span>
                        ) : (() => {
                          const col = columns.find(c => c.key === columnId);
                          return col ? col.render(item) : null;
                        })()}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                  {isExpanded && (
                    <div className={styles.expandedContent}>
                      {renderExpanded(item, debouncedSearch)}
                    </div>
                  )}
                </React.Fragment>
              );
            }}
          </DataGridBody>
        </DataGrid>
      )}
    </div>
  );
}

// Filter dropdown popover for column headers
function FilterDropdown(props: {
  values: string[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
}) {
  const styles = useStyles();
  const hasFilter = props.selected.size > 0;

  return (
    <Popover withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<FilterRegular />}
          style={hasFilter ? { color: tokens.colorBrandForeground1 } : undefined}
          aria-label="Filter column"
          onClick={(e) => e.stopPropagation()}
        />
      </PopoverTrigger>
      <PopoverSurface onClick={(e) => e.stopPropagation()}>
        <div className={styles.filterPopoverContent}>
          {hasFilter && (
            <Button size="small" appearance="subtle" onClick={props.onClear}>
              Clear filter
            </Button>
          )}
          <div className={styles.filterList}>
            {props.values.map((v) => (
              <Checkbox
                key={v}
                label={v || '(empty)'}
                checked={props.selected.has(v)}
                onChange={() => props.onToggle(v)}
              />
            ))}
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

export default ExpandableTable;
